import React, { useCallback, useMemo, useRef, useState, useEffect, useLayoutEffect } from "react";
import html2canvas from "html2canvas";
import { auth } from "./firebase/firebase";
import { motion } from "framer-motion";
import './SliderStyles.css';
import { submitChronoScore } from "./firebase/submitChronoScore";
import { format } from "date-fns";
import { markDailyGamePlayed } from "./firebase/hasPlayedDaily";
import { AnimatePresence } from "framer-motion";
import PostGameMenu from "./Components/PostGameMenu";
import ParticleExplosion from "./Components/ParticleExplosion";
import { getGuessCountLabel, resolveChronoscore, resolveGuessCount } from "./gameSession";
import useAccessibleDialog from "./useAccessibleDialog";
import { getYearFontSize, getYearGapLayout } from "./superScoreLayout";

const calculateYearScore = (guess, actual) => {
  const diff = Math.abs(guess - actual);
  if (diff === 0) return 10;
  if (diff <= 2) return 9;
  if (diff <= 5) return 8;
  if (diff <= 10) return 7;
  if (diff <= 25) return 6;
  if (diff <= 50) return 5;
  if (diff <= 75) return 4;
  if (diff <= 100) return 3;
  if (diff <= 200) return 2;
  if (diff <= 500) return 1;
  return 0;
};

const useWindowHeight = () => {
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => setHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return height;
};



const formatYear = (year) => (year < 0 ? `${Math.abs(year)} BCE` : `${year}`);

const SuperScoreRound = ({
  playerOrder,
  chronoscore,
  onScoreFinalized,
  onScoreUpdate,
  isSuperScore,
  displayName,
  currentDay,
  isPremiumMode,
  setScoreFinalized,
  setActivePanel,
  setShowLocker,
  onPlayUnlimited, // ✅ Add this
  numGuesses, // ✅ Add this
  testData = null,
}) => {
  const resolvedChronoscore = resolveChronoscore(chronoscore, numGuesses);
  const [currentIndex, setCurrentIndex] = useState(() => testData?.currentIndex ?? 0);
  const [guess, setGuess] = useState(() => testData?.guess ?? 1000);
  const [timelineStart, setTimelineStart] = useState(() => testData?.timelineStart ?? 0);
  const timelineEnd = 2025;
  const [hasSubmittedThisRound, setHasSubmittedThisRound] = useState(() => testData?.hasSubmittedThisRound ?? false);
  const [submittedGuess, setSubmittedGuess] = useState(() => testData?.submittedGuess ?? null);
  const [yearsAway, setYearsAway] = useState(() => testData?.yearsAway ?? 0);
  const [showFeedback, setShowFeedback] = useState(() => testData?.showFeedback ?? false);
  const [showPoints, setShowPoints] = useState(() => testData?.showPoints ?? false);
  const [allGuesses, setAllGuesses] = useState(() => testData?.allGuesses ?? []);
  const [finalScore, setFinalScore] = useState(() => testData?.finalScore ?? null);
  const [actualYearFrozen, setActualYearFrozen] = useState(() => testData?.actualYearFrozen ?? null);
  const nextClickedRef = useRef(false);
  const windowHeight = useWindowHeight();
  const isCompactLayout = window.innerWidth < 360 || windowHeight < 700;
  const isShortLandscape = windowHeight <= 500;
  const [animatedPoints, setAnimatedPoints] = useState(() => testData?.animatedPoints ?? 0);
  const currentEvent = useMemo(
    () => playerOrder[currentIndex] || playerOrder[0] || { event: "", year: 0 },
    [currentIndex, playerOrder]
  );
  const [, setShowAnimatedPoints] = useState(false);
  const sliderRef = useRef();
  const scrollCardRef = useRef(null);
  const [yearLayout, setYearLayout] = useState(null);
  const [finalPoints, setFinalPoints] = useState(() => testData?.finalPoints ?? 0);
  const isSharing = false;
  const [shareImageUrl, setShareImageUrl] = useState(() => testData?.shareImageUrl ?? null); // ✅ Fallback image modal
  const shareDialogRef = useAccessibleDialog(() => setShareImageUrl(null), Boolean(shareImageUrl));

  const [showProfileMenu, setShowProfileMenu] = useState(false); // ✅ Menu state
  const captureCardRef = useRef(null); // ✅ Ref for image capture
  const [readyToShareData, setReadyToShareData] = useState(null); // ✅ Pre-generated share payload
  const [showExplosion, setShowExplosion] = useState(false);
  const [feedbackText, setFeedbackText] = useState(() => testData?.feedbackText ?? "Drag the slider below to your best guess");
  const [hasRestored, setHasRestored] = useState(() => Boolean(testData));
  const finalTableCellPadding = isShortLandscape
    ? "0.2rem 0.3rem"
    : isCompactLayout ? "0.5rem 0.35rem" : "0.75rem 0.5rem";
  const finalButtonHeight = isShortLandscape ? "2.5rem" : isCompactLayout ? "3.25rem" : "3.75rem";
  const finalButtonFontSize = isShortLandscape ? "1.1rem" : isCompactLayout ? "1.25rem" : "1.5rem";

  // 💾 Persistence Helper
  const getStorageKey = useCallback(() => {
    const uid = auth.currentUser?.uid || localStorage.getItem("guestUid");
    if (!uid || !currentDay) return null;
    return `chrono_super_state_${currentDay}_${uid}`;
  }, [currentDay]);

  // 💾 Save Effect
  useEffect(() => {
    if (testData) return undefined;
    if (!currentDay || !playerOrder || playerOrder.length === 0 || !hasRestored) return;

    const key = getStorageKey();
    if (!key) return;

    const state = {
      currentIndex,
      allGuesses,
      finalScore,
      submittedGuess,
      yearsAway,
      showFeedback,
      showPoints,
      actualYearFrozen,
      hasSubmittedThisRound,
      timelineStart,
      guess,
      currentDay
    };

    localStorage.setItem(key, JSON.stringify(state));
  }, [currentIndex, allGuesses, finalScore, submittedGuess, yearsAway, showFeedback, showPoints, actualYearFrozen, hasSubmittedThisRound, timelineStart, guess, currentDay, hasRestored, playerOrder, testData, getStorageKey]);

  // 💾 Load Effect
  useEffect(() => {
    if (testData) return undefined;
    if (!currentDay || !playerOrder || playerOrder.length === 0) return;

    const key = getStorageKey();
    if (!key) {
      setHasRestored(true);
      return;
    }

    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.currentDay === currentDay) {
          console.log("🔄 Restoring Super Score state...");
          setCurrentIndex(parsed.currentIndex);
          setAllGuesses(parsed.allGuesses);
          setFinalScore(parsed.finalScore);
          setSubmittedGuess(parsed.submittedGuess);
          setYearsAway(parsed.yearsAway);
          setShowFeedback(parsed.showFeedback);
          setShowPoints(parsed.showPoints);
          setActualYearFrozen(parsed.actualYearFrozen);
          setHasSubmittedThisRound(parsed.hasSubmittedThisRound);
          setTimelineStart(parsed.timelineStart);
          setGuess(parsed.guess);

          // 🛠️ Mid-animation refresh recovery
          if (parsed.hasSubmittedThisRound && !parsed.showPoints && playerOrder[parsed.currentIndex]) {
            const actualYear = playerOrder[parsed.currentIndex].year;
            const finalDistance = Math.abs(parsed.submittedGuess - actualYear);
            setYearsAway(finalDistance);
            setShowPoints(true);
            setActualYearFrozen(actualYear);
          }
        }
      } catch (e) {
        console.error("Failed to restore Super Score state", e);
      }
    }
    setHasRestored(true);
  }, [currentDay, playerOrder, testData, getStorageKey]);


  const getPointsColor = (points) => {
    if (points === 10) return "#FFD700"; // Gold
    if (points === 9) return "#ff69b4";  // Pink
    if (points === 8 || points === 7) return "#a855f7"; // Lighter Purple
    if (points === 6 || points === 5) return "#1e90ff"; // Blue
    if (points === 4 || points === 3 || points === 2) return "#3bfd7a"; // Green
    if (points === 1) return "#3bfd7a";
    return "#e63d3d"; // 0 points (Red)
  };

  useLayoutEffect(() => {
    const updateYearTop = () => {
      if (scrollCardRef.current && sliderRef.current) {
        const scrollBottom = scrollCardRef.current.getBoundingClientRect().bottom;
        const sliderTrack = sliderRef.current.querySelector(".timeline-slider");
        const sliderTop = sliderTrack
          ? sliderTrack.getBoundingClientRect().top
          : sliderRef.current.getBoundingClientRect().top;
        setYearLayout(getYearGapLayout(scrollBottom, sliderTop));
      }
    };

    updateYearTop();
    window.addEventListener("resize", updateYearTop);

    const observer = new ResizeObserver(() => updateYearTop());
    const scrollCardEl = scrollCardRef.current;
    if (scrollCardEl) {
      observer.observe(scrollCardEl);
    }
    const sliderEl = sliderRef.current;
    if (sliderEl) {
      observer.observe(sliderEl);
    }

    return () => {
      window.removeEventListener("resize", updateYearTop);
      if (scrollCardEl) {
        observer.unobserve(scrollCardEl);
      }
      if (sliderEl) {
        observer.unobserve(sliderEl);
      }
    };
  }, []);

  useEffect(() => {
    if (testData) return undefined;
    if (!showPoints || submittedGuess === null || !hasSubmittedThisRound) return;

    const target = calculateYearScore(guess, currentEvent.year);
    setFinalPoints(target); // ✅ Set once here and use in render

    // if (target <= 1) {
    //   setAnimatedPoints(target);
    //   return;
    // }

    const duration = target <= 1 ? 300 : 1000; // Faster for small numbers
    const start = performance.now();
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const animate = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);
      // ✅ Start at 1 to avoid red flash (unless target is actually 0, which is handled elsewhere)
      const current = Math.max(1, Math.round(eased * target));
      setAnimatedPoints(current);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [showPoints, submittedGuess, guess, currentEvent, hasSubmittedThisRound, testData]);



  useEffect(() => {
    if (testData) return undefined;
    if (!currentEvent || !hasRestored) return;

    // 🛑 GUARD: If we just restored and we've already submitted this round,
    // don't reset the state! This prevents a refresh from wiping the feedback screen.
    if (hasSubmittedThisRound) return;

    setAnimatedPoints(0); // ✅ reset on new event

    if (currentIndex === 0) {
      const rawStart = currentEvent.year - Math.floor(Math.random() * 1001);
      setTimelineStart(rawStart);
      const midpoint = Math.round((rawStart + timelineEnd) / 2);
      setGuess(midpoint);
    } else {
      const previousActualYear = playerOrder[currentIndex - 1].year;
      setTimelineStart(previousActualYear);
      const midpoint = Math.round((previousActualYear + timelineEnd) / 2);
      setGuess(midpoint);
    }

    setSubmittedGuess(null);
    setShowFeedback(false);
    setShowPoints(false);
    setShowAnimatedPoints(false);
    setShowExplosion(false);
    setFeedbackText("Drag the slider below to your best guess");
    setYearsAway(0);
    setActualYearFrozen(null);
    nextClickedRef.current = false; // ✅ ready for new animation
  }, [currentEvent, currentIndex, hasRestored, hasSubmittedThisRound, playerOrder, testData]);



  // ✅ AUTO-GENERATE IMAGE when score is final
  useEffect(() => {
    if (testData) return undefined;
    if (finalScore === null || !captureCardRef.current || readyToShareData) return;

    const generate = async () => {
      try {
        // Wait a tick for render
        await new Promise(r => setTimeout(r, 500));

        const canvas = await html2canvas(captureCardRef.current, {
          useCORS: true,
          scale: 2,
          backgroundColor: "#034c39",
        });

        // Generate Blob for sharing (JPEG for better preview support)
        const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
        const blob = await (await fetch(dataUrl)).blob();

        // Use displayName if available, otherwise "My"
        const cleanName = displayName ? displayName.replace(/[^a-zA-Z0-9]/g, "") : "My";
        const filename = `${cleanName}Chronoscore${currentDay}.jpg`;

        const file = new File([blob], filename, { type: "image/jpeg", lastModified: Date.now() });
        const shareData = { files: [file] };

        // Save for later
        setReadyToShareData({ shareData, blob, dataUrl });
      } catch (err) {
        console.warn("Pre-generation failed", err);
      }
    };

    generate();
  }, [finalScore, readyToShareData, displayName, currentDay, testData]);


  const guessSoundRef = useRef(null);

  useEffect(() => {
    guessSoundRef.current = new Audio("/sounds/guesssfx.wav");
    guessSoundRef.current.volume = 0.02;
    guessSoundRef.current.preload = "auto";
  }, []);

  const handleSubmit = () => {
    if (hasSubmittedThisRound) return;

    const actualYear = currentEvent.year;
    const distance = Math.abs(guess - actualYear);
    const earned = calculateYearScore(guess, actualYear);

    setSubmittedGuess(guess);
    setShowFeedback(true);
    setHasSubmittedThisRound(true);

    // 💾 Add to allGuesses IMMEDIATELY for persistence
    // This ensures that even a refresh during the animation delay saves the data.
    const newGuess = {
      event: currentEvent.event,
      guess,
      year: actualYear,
      points: earned,
    };

    setAllGuesses((prevGuesses) => {
      const updated = [...prevGuesses, newGuess];
      if (typeof onScoreUpdate === "function") {
        onScoreUpdate(updated.reduce((sum, g) => sum + g.points, 0));
      }
      return updated;
    });

    if (distance === 0) {
      setYearsAway(0);
      setShowPoints(true);
      setShowAnimatedPoints(true);
      setShowExplosion(true);
      setFeedbackText("Congratulations! You guessed exactly right!");

      const successSound = new Audio("/sounds/successsfx.mp3");
      successSound.play().catch(() => { });
      return;
    }

    const duration = 4000;
    const startTime = performance.now();
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const rawProgress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(rawProgress);
      const currentCount = Math.round(easedProgress * distance);
      setYearsAway(currentCount);
      if (rawProgress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
    setTimeout(() => {
      if (guessSoundRef.current) {
        guessSoundRef.current.currentTime = 0;
        guessSoundRef.current.play().catch(() => { });
      }
      setShowPoints(true);
      setActualYearFrozen(actualYear);
    }, duration);
  };

  const next = async () => {
    nextClickedRef.current = true;

    // If we're on the last question, skip animations and go straight to final
    if (currentIndex === playerOrder.length - 1) {
      const total = allGuesses.reduce((sum, g) => sum + g.points, 0) + resolvedChronoscore;
      const dateKey = format(new Date(), "yyyy-MM-dd");

      try {
        // ✅ Await the score submission to ensure it saves before unmounting
        await submitChronoScore({
          dateKey,
          guesses: resolveGuessCount(numGuesses, resolvedChronoscore),
          chronoscore: total,
          events: allGuesses,
        });

        if (!isPremiumMode) {
          // ✅ CRITICAL FIX: Use UID, not DisplayDisplayName
          const uid = auth.currentUser?.uid || localStorage.getItem("guestUid");
          if (uid) {
            // ✅ Await this too
            await markDailyGamePlayed(uid, currentDay);
          }
        }
      } catch (error) {
        console.warn("Score submission handled error:", error);
        // We proceed to show the score screen even if submission failed (e.g. already played)
      }

      onScoreFinalized(total);
      if (typeof setScoreFinalized === "function") {
        setScoreFinalized(true);
      }

      // No transition animation here — immediately clear state
      setShowPoints(false);
      setShowFeedback(false);
      setYearsAway(0);
      setActualYearFrozen(null);
      setHasSubmittedThisRound(false);
      setHasSubmittedThisRound(false);
      setFinalScore(total);
      setShowExplosion(false);
      return;
    }

    // Otherwise, do transition as normal
    setTimeout(() => {
      setShowPoints(false);
      setShowFeedback(false);
      setShowExplosion(false);
      setFeedbackText("Drag the slider below to your best guess");
      setYearsAway(0);
      setActualYearFrozen(null);
      setHasSubmittedThisRound(false);

      setCurrentIndex((i) => i + 1);
    }, 200);
  };
  if (finalScore !== null) {
    return (


      <div
        style={{
          color: "#e7c17f",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: isShortLandscape ? "flex-start" : "center",
          position: "fixed",
          inset: 0,
          width: "100%",
          height: "100dvh",
          zIndex: 10000,
          boxSizing: "border-box",
          background: "radial-gradient(circle at center, #03715f, #034c39)",
          fontFamily: "'Baskerville', 'Garamond', 'Georgia', 'serif'",
          fontWeight: "bold",
          overflowY: "auto", // ✅ Allow scrolling if content is too tall
          padding: isShortLandscape ? "0.25rem" : isCompactLayout ? "0.75rem" : "1rem",
        }}
      >
        <div
          data-testid="final-score-content"
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: isShortLandscape ? "0.25rem" : isCompactLayout ? "0.75rem" : "1.5rem",
            flexShrink: 0,
          }}
        >
        {/* 1. CONTENT AREA (Title + Table) - No longer flex:1 */}
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          flexShrink: 0, // Don't shrink
        }}>
          <h1
            style={{
              fontSize: isShortLandscape ? "1.35rem" : isCompactLayout ? "1.5rem" : "1.8rem",
              textAlign: "center",
              margin: `0 0 ${isShortLandscape ? "0.1rem" : isCompactLayout ? "0.5rem" : "1rem"} 0`,
            }}
          >
            {displayName ? `${displayName}'s Chronoscore:` : "Chronoscore:"} {finalScore}
          </h1>

          <table
            style={{
              borderCollapse: "collapse",
              // fontFamily inherited now
              fontSize: isShortLandscape ? "0.85rem" : isCompactLayout ? "0.9rem" : "1rem",
              color: "#e7c17f",
              width: "90%",
              maxWidth: "600px",
              padding: "0",
              margin: "0 auto",
            }}
          >
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid rgba(231, 193, 127, 0.3)" }}>
                <th style={{ padding: finalTableCellPadding, width: "40%" }}>Event</th>
                <th style={{ textAlign: "right", padding: finalTableCellPadding }}>Guess</th>
                <th style={{ textAlign: "right", padding: finalTableCellPadding }}>Actual</th>
                <th style={{ textAlign: "right", padding: finalTableCellPadding }}>Points</th>
              </tr>
            </thead>
            <tbody>
              {allGuesses.map((g, i) => (
                <tr
                  key={i}
                  style={{
                    color: "#e7c17f", // Uniform color
                  }}
                >
                  <td style={{
                    padding: finalTableCellPadding,
                    textAlign: "left",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "200px" // 👈 Truncate long text
                  }}>
                    {g.event}
                  </td>
                  <td style={{ padding: finalTableCellPadding, textAlign: "right" }}>{formatYear(g.guess)}</td>
                  <td style={{ padding: finalTableCellPadding, textAlign: "right" }}>{formatYear(g.year)}</td>
                  <td style={{ textAlign: "right", padding: finalTableCellPadding }}>{g.points}</td>
                </tr>
              ))}
              {/* Divider + Guess Count row */}
              <tr style={{ borderTop: "none" }}>
                <td
                  colSpan={3}
                  style={{
                    textAlign: "left",
                    padding: finalTableCellPadding,

                  }}
                >
                  {getGuessCountLabel(numGuesses, resolvedChronoscore)}
                </td>
                <td
                  style={{
                    textAlign: "right",
                    padding: finalTableCellPadding,
                    color: "#e7c17f",
                  }}
                >
                  {resolvedChronoscore}
                </td>
              </tr>
              <tr style={{ borderTop: "1px solid rgba(231, 193, 127, 0.3)" }}>
                <td
                  colSpan={3}
                  style={{
                    textAlign: "left",
                    padding: finalTableCellPadding,
                    fontWeight: "bold", // already bold via container, but good to keep
                  }}
                >
                  Total
                </td>
                <td
                  style={{
                    textAlign: "right",
                    padding: finalTableCellPadding,
                    fontWeight: "bold",
                    color: "#e7c17f",
                  }}
                >
                  {finalScore}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* 2. BUTTONS AREA */}
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "90%",
          maxWidth: "600px",
          paddingBottom: "0",
          gap: isShortLandscape ? "0.2rem" : "0.5rem",
          flexShrink: 0, // Don't shrink
        }}>
          <button
            className="submit-button"
            style={{
              border: "none",
              height: finalButtonHeight,
              fontWeight: "bold",
              fontSize: finalButtonFontSize,
              width: "100%",
              padding: "0",
              borderRadius: "8px",
              boxShadow: "none",
              background: "linear-gradient(135deg, #002d21, #00372a)",
              color: "#e7c17f",
              margin: 0,
            }}
            onClick={async () => {
              // 1. If we have pre-generated data, try to share immediately (Synchronous-ish)
              if (readyToShareData && navigator.share) {
                try {
                  if (navigator.canShare && navigator.canShare(readyToShareData.shareData)) {
                    await navigator.share(readyToShareData.shareData);
                  } else {
                    // Fallback attempt
                    await navigator.share(readyToShareData.shareData);
                  }
                } catch (err) {
                  if (err.name !== "AbortError") {
                    console.warn("Share failed", err);
                    alert(`Share failed: ${err.message}`);
                    setShareImageUrl(readyToShareData.dataUrl);
                  }
                }
                return;
              }

              // 2. Not ready? Show modal fallback directly if we have data, or wait
              if (readyToShareData) {
                setShareImageUrl(readyToShareData.dataUrl);
              } else {
                alert("Generating image... try again in a second!");
              }
            }}
          >
            {isSharing ? "Generating..." : "Share Score"}
          </button>
          <button
            className="submit-button"
            style={{
              border: "none",
              height: finalButtonHeight,
              fontWeight: "bold",
              fontSize: finalButtonFontSize,
              width: "100%",
              padding: "0",
              borderRadius: "8px",
              boxShadow: "none",
              background: "linear-gradient(135deg, #002d21, #00372a)",
              color: "#e7c17f",
              margin: 0,
            }}
            onClick={() => setActivePanel("stats")}
          >
            My Stats
          </button>
          <button
            className="submit-button"
            style={{
              border: "none",
              height: finalButtonHeight,
              fontWeight: "bold",
              fontSize: finalButtonFontSize,
              width: "100%",
              padding: "0",
              borderRadius: "8px",
              boxShadow: "none",
              background: "linear-gradient(135deg, #002d21, #00372a)",
              color: "#e7c17f",
              margin: 0,
            }}
            onClick={() => setActivePanel("leaderboard")}
          >
            Leaderboard
          </button>
        </div>

        </div>

        {/* ✅ Share Image Modal Fallback */}
        {
          shareImageUrl && (
            <div
              ref={shareDialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="share-score-dialog-title"
              aria-describedby="share-score-dialog-description"
              tabIndex={-1}
              style={{
                position: "fixed",
                top: 0, left: 0, right: 0, bottom: 0,
                background: "rgba(0,0,0,0.85)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 20000,
                padding: "1rem",
              }}
              onClick={() => {
                setShareImageUrl(null);
              }}
            >
              <div id="share-score-dialog-title" className="visually-hidden">Share your Chronologicle score</div>
              <div id="share-score-dialog-description" style={{ color: "#e7c17f", marginBottom: "1rem", fontSize: "1.2rem", textAlign: "center" }}>Long press or right click to save image.
              </div>
              <img
                src={shareImageUrl}
                alt="Chronologicle score card preview"
                style={{ maxWidth: "100%", maxHeight: "60vh", borderRadius: "8px", boxShadow: "0 0 20px rgba(0,0,0,0.5)" }}
                onClick={(e) => e.stopPropagation()} // Allow clicking image without closing
              />

              {/* ✅ NEW SHARE BUTTON IN MODAL */}
              {navigator.share && readyToShareData && (
                <button
                  style={{
                    marginTop: "1.5rem",
                    background: "#e7c17f",
                    border: "none",
                    color: "#002d21",
                    padding: "0.8rem 2rem",
                    borderRadius: "20px",
                    fontSize: "1.2rem",
                    fontWeight: "bold",
                    cursor: "pointer",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.3)"
                  }}
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      await navigator.share(readyToShareData.shareData);
                    } catch (err) {
                      alert(`Share failed again: ${err.message}`);
                    }
                  }}
                >
                  Share Image
                </button>
              )}
              <button
                style={{
                  marginTop: "1.5rem",
                  background: "transparent",
                  border: "1px solid #e7c17f",
                  color: "#e7c17f",
                  padding: "0.5rem 1.5rem",
                  borderRadius: "20px",
                  fontSize: "1rem",
                  cursor: "pointer"
                }}
                onClick={() => setShareImageUrl(null)}
                data-dialog-initial-focus
              >
                Close
              </button>
            </div>
          )
        }

        {/* ✅ Render Menu Overlay */}
        {
          showProfileMenu && (
            <PostGameMenu
              onClose={() => setShowProfileMenu(false)}
              onStats={() => {
                setShowProfileMenu(false);
                setActivePanel("stats");
              }}
              onLeaderboard={() => {
                setShowProfileMenu(false);
                setActivePanel("leaderboard");
              }}
              onUnlimited={() => {
                setShowProfileMenu(false);
                onPlayUnlimited();
              }}
            />
          )
        }

        <div
          ref={captureCardRef}
          style={{
            position: "absolute",
            top: "-9999px",
            left: "-9999px",
            width: "600px",
            height: "600px",
            backgroundImage: "url(/share_bg.png)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif",
            fontWeight: "900", // Heavy
            color: "#e7c17f",
            boxSizing: "border-box",
            padding: "1rem 1.5rem 1.5rem 1.5rem", // Less padding on top to raise header
          }}
        >
          {/* Top Row */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
            <div style={{ fontSize: "2.4rem", fontWeight: "bold" }}>Chronologicle.com</div>
            <div style={{ fontSize: "2.4rem", fontWeight: "bold" }}>#{currentDay}</div>
          </div>

          {/* Center Content */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1 }}>
            <div style={{ fontSize: "2.5rem", fontWeight: "bold", marginBottom: "0.2rem" }}>I Scored</div>
            <div style={{
              fontSize: "6rem",
              fontWeight: "bold",
              lineHeight: "1",
              color: "#e7c17f"
            }}>
              {finalScore}
            </div>
          </div>

          {/* Bottom Row */}
          <div style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
            <div style={{ fontSize: "2.4rem", fontWeight: "bold" }}>
              {format(new Date(), "MMMM d, yyyy")}
            </div>
          </div>
        </div>
      </div >
    );
  }

  return (

    //start of 2nd round styling
    <div
      style={{

        color: "#e7c17f",
        textAlign: "center",
        minHeight: "100dvh",
        padding: isCompactLayout ? "1rem" : "4.5rem",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center", alignContent: "center", justifyContent: "flex-start", // ✅ stays fixed at the top
      }}
    >
      {/* Confetti Explosion for Perfect Guess */}
      {showExplosion && <ParticleExplosion />}
      <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>



        <div
          style={{
            position: "relative",

            width: "min(100%, 1200px)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: isCompactLayout ? "1.5rem" : "4rem",
            textAlign: "center",
          }}
        >


          <div style={{
            position: "fixed",
            top: isShortLandscape ? "0.25rem" : isCompactLayout ? "4rem" : "clamp(6rem,13vh,12rem)", justifyContent: "center", left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            zIndex: 2,
          }}>


            <div
              className="guess-instruction"
              style={{
                fontSize: isShortLandscape ? "0.9rem" : "1.2rem",
                fontFamily: "'Baskerville', 'Garamond', 'Georgia', serif",
                color: "#e7c17f",
                margin: "0",
                minHeight: isShortLandscape ? "1.05rem" : "1.5rem",
                maxWidth: "min(100%, 18rem)",
                lineHeight: 1.2,
                padding: "0 0.5rem",
                textAlign: "center", justifyContent: "center", overflow: "hidden",
                whiteSpace: "normal",
                zIndex: 2,
              }}
            >
              {feedbackText}
            </div>

            {/* 🟫 Scroll Card */}
            <div ref={scrollCardRef}
              className="scroll-card"
              style={{
                width: "min(75vw, 1200px)",
                // responsive range

                marginLeft: isCompactLayout ? "2.25rem" : "3rem", marginRight: isCompactLayout ? "2.25rem" : "3rem",
                padding: isShortLandscape ? "0.45rem" : "1rem",
                marginTop: isShortLandscape ? "0.3rem" : isCompactLayout ? "1rem" : "2rem",
                fontSize: isShortLandscape ? "1rem" : "clamp(.5rem, 6vw, 1.5rem)",
                textAlign: "center",
                marginBottom: "0",
                zIndex: 2,

              }}
            >

              {currentEvent.event}
            </div>


          </div>



          {submittedGuess === null && yearLayout && (
            <div
              data-testid="guess-year"
              style={{
                position: "fixed",
                top: `${yearLayout.top}px`,
                left: "50%",
                transform: "translate(-50%, -50%)",
                fontSize: `${getYearFontSize({
                  guess,
                  availableHeight: yearLayout.availableHeight,
                  viewportWidth: window.innerWidth,
                })}px`,
                lineHeight: 0.9,
                fontWeight: "bold",
                color: "#e7c17f",
                overflow: "hidden",
                whiteSpace: "nowrap",
                fontFamily: "'Garamond', serif",
                zIndex: 1,
              }}
            >
              {formatYear(guess)}
            </div>
          )}







          <AnimatePresence mode="wait">
            {submittedGuess !== null && (
              <div
                style={{
                  textAlign: "center",
                  textShadow: "0 0 6px rgba(255, 255, 200, 0.1)",
                  fontWeight: "bold",
                  fontSize: "clamp(1rem, 5vh, 2.5rem)",
                  marginBottom: guess < 0 ? "8.05rem" : "0",
                  marginTop: "clamp(10rem, 30vh,15rem)",
                  width: "100%",
                  flexDirection: "column",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  gap: "0.6rem",
                }}
              >

                <div style={{
                  overflow: "hidden",
                  whiteSpace: "nowrap", lineHeight: "clamp(3rem, 8vh,4rem)",
                }}>{`Guess: ${formatYear(guess)}`}</div>

                <div style={{
                  overflow: "hidden",
                  whiteSpace: "nowrap", lineHeight: "clamp(3rem, 8vh,4rem)", color: "#eaca94"
                }}>
                  {submittedGuess === currentEvent.year
                    ? "Correct Year"
                    : showPoints
                      ? yearsAway === 0
                        ? "0 Years Away"
                        : `Actual: ${formatYear(actualYearFrozen)}`
                      : `${yearsAway} Years Away`}
                </div>
                <div
                  style={{
                    visibility: showPoints ? "visible" : "hidden",
                    color: getPointsColor(calculateYearScore(guess, currentEvent.year)),
                    textShadow: "none",
                    minHeight: "1.5rem", // avoids shifting
                    lineHeight: "clamp(3rem, 8vh,4rem)",

                    fontSize: "clamp(1rem, 5vh, 2.5rem)",
                  }}
                >
                  <motion.div
                    key={`${currentIndex}-${finalPoints}`} // Re-triggers animation each round
                    initial={{ scale: 0.6, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, type: "spring", stiffness: 140 }}
                    style={{
                      visibility: showPoints ? "visible" : "hidden",
                      color: getPointsColor(finalPoints), // ✅ base on finalPoints, not animatedPoints
                      textShadow: "none",
                      minHeight: "1.5rem",
                      lineHeight: "clamp(3rem, 8vh,4rem)",
                      fontSize: "clamp(1rem, 5vh, 2.5rem)",
                    }}
                  >
                    {`+${animatedPoints} ${finalPoints === 1 ? "Point" : "Points"}`}
                  </motion.div>
                </div>

              </div>
            )}
          </AnimatePresence>

        </div>


      </div>


      {submittedGuess === null && (
        <div ref={sliderRef}
          className="slider-container"
          style={{
            bottom: isShortLandscape ? "0.25rem" : undefined,
            padding: isShortLandscape ? "0.5rem" : undefined,
          }}
        >

          <input
            type="range"
            className="timeline-slider"
            min={timelineStart}
            max={timelineEnd}
            value={guess}
            onChange={(e) => setGuess(Number(e.target.value))}
            style={{
              width: "100%",
              height: isShortLandscape ? "1rem" : "1.3rem",
              marginBottom: isShortLandscape ? "0.25rem" : undefined,
              borderRadius: "1rem", zIndex: 999, // 👈 HIGHER z-index (slider on top)

            }}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: isShortLandscape ? "0.95rem" : "1.3rem",
              fontWeight: "bold",
              color: "#e7c17f",
              marginTop: isShortLandscape ? "0.2rem" : "0.5rem",
            }}
          >
            <span>{formatYear(timelineStart)}</span>
            <span>{formatYear(timelineEnd)}</span>
          </div>

          <motion.div
            initial={false}
            animate={{
              opacity: !showFeedback ? 1 : 0,
              pointerEvents: !showFeedback ? "auto" : "none",
            }}
            transition={{ duration: 0.1 }}
            style={{ marginTop: isShortLandscape ? "0.4rem" : "1.5rem", width: "100%" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: ".4rem",
                width: "100%",
              }}
            >
              <button
                onClick={() => setGuess((prev) => Math.max(timelineStart, prev - 1))}
                disabled={submittedGuess !== null}
                className="submit-button"
                style={{
                  flex: "0 0 3rem", // 👈 fixed width
                  height: isShortLandscape ? "2.25rem" : "4rem",
                  fontWeight: "bold",
                  fontSize: "1.5rem",
                  border: "none",
                  boxShadow: "none",
                }}
              >
                -
              </button>

              <button
                onClick={handleSubmit}
                disabled={submittedGuess !== null}
                className="submit-button"
                style={{
                  flex: "2",
                  height: isShortLandscape ? "2.25rem" : "4rem",
                  fontWeight: "bold",
                  fontSize: "1.5rem",
                  border: "none",
                  boxShadow: "none",
                }}
              >
                Guess
              </button>

              <button
                onClick={() => setGuess((prev) => Math.min(timelineEnd, prev + 1))}
                disabled={submittedGuess !== null}
                className="submit-button"
                style={{
                  flex: "0 0 3rem", // 👈 fixed width
                  height: isShortLandscape ? "2.25rem" : "4rem",
                  fontWeight: "bold",
                  fontSize: "1.5rem",
                  border: "none",
                  boxShadow: "none",
                }}
              >
                +
              </button>
            </div>
          </motion.div>
        </div>
      )}








      {showFeedback && (
        <>


          {showPoints && (
            <motion.div

              style={{

                width: "100%",
              }}
            >
              <div className="continue"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  gap: ".4rem", // optional, just for visual consistency
                }}
              >
                <button
                  onClick={next}
                  className="submit-button"
                  style={{
                    flex: "2",
                    height: "4rem",
                    fontWeight: "bold",
                    fontSize: "1.5rem",
                    border: "none",
                    boxShadow: "none",

                  }}
                >
                  Continue
                </button>
              </div>
            </motion.div>
          )}
        </>
      )}

    </div>

  );

};

export default SuperScoreRound;
